import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type SampleUsersResponse } from '../services/types';

interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage?: string; // Optional message from the server
  data?: SampleUsersResponse; // The actual data returned by the API
  error?: string | null; // Error details in case of failure
  status?: number;
  ok?: boolean;
}

/**
 * Fetches sample users for a specific rule key from the external API service.
 *
 * @async
 * @function fetchSampleUsers
 * @param {number} ruleKey - The key associated with a specific rule to fetch its sample users.
 * @returns {Promise<SampleUser[]>} A promise that resolves to an array of sample users.
 */
const fetchSampleUsers = async (ruleKey: number) => {
  const response = (await ExternalService.getRuleSampleUsers(
    ruleKey
  )) as ApiResponse;

  return response;
};

/**
 * Custom hook to retrieve sample users using React Query's `useMutation`.
 *
 * @function useGetSampleUsers
 * @returns {import('@tanstack/react-query').UseMutationResult<SampleUser[], unknown, number>} A mutation result object containing the data, loading state, and potential errors.
 */
const useGetSampleUsers = () =>
  useMutation({
    mutationFn: (ruleKey: number) => fetchSampleUsers(ruleKey), // Function to fetch data
  });

export default useGetSampleUsers;
